import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import AnalyticsContext from '../contexts/AnalyticsContext';
import VersionContext from '../contexts/VersionContext';
import getCurrentBuildNumber from '../api/getCurrentBuildNumber';
import reloadWhenSafe from '../reloadWhenSafe';
import AppUpdateBanner from '../components/AppUpdateBanner';
import Flash from '../components/Flash';

const SNOOZE_KEY = 'appUpdateSnoozedUntil';
const RELOAD_KEY = 'appUpdateReload';
const SNOOZE_DURATION_MS = 10 * 60 * 1000;

const getStoredSnoozedUntil = (): number | null => {
  const stored = window.localStorage.getItem(SNOOZE_KEY);
  if (!stored) {
    return null;
  }
  const until = parseInt(stored, 10);
  return until > Date.now() ? until : null;
};

const VersionProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const { trackEvent } = useContext(AnalyticsContext);
  const [isFlashVisible, setIsFlashVisible] = useState<boolean>(false);
  const [buildNumber, setBuildNumber] = useState<number | undefined>();
  const [hasUpdate, setHasUpdate] = useState<boolean>(false);
  const [snoozedUntil, setSnoozedUntil] = useState<number | null>(
    getStoredSnoozedUntil,
  );
  const snoozeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // If a tab opens mid-snooze, restore the expiry timeout
  useEffect(() => {
    if (!snoozedUntil) {
      return;
    }
    const remaining = snoozedUntil - Date.now();
    if (remaining <= 0) {
      setSnoozedUntil(null);
      return;
    }
    snoozeTimeoutRef.current = setTimeout(
      () => setSnoozedUntil(null),
      remaining,
    );
    return () => {
      if (snoozeTimeoutRef.current) {
        clearTimeout(snoozeTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty — only for initial snooze restoration

  // Cross-tab sync via storage events
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === SNOOZE_KEY) {
        const until = e.newValue ? parseInt(e.newValue, 10) : null;
        setSnoozedUntil(until);
        if (snoozeTimeoutRef.current) {
          clearTimeout(snoozeTimeoutRef.current);
        }
        if (until && until > Date.now()) {
          snoozeTimeoutRef.current = setTimeout(
            () => setSnoozedUntil(null),
            until - Date.now(),
          );
        }
      }
      if (e.key === RELOAD_KEY && e.newValue) {
        reloadWhenSafe();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const handleDismiss = useCallback(() => {
    const until = Date.now() + SNOOZE_DURATION_MS;
    window.localStorage.setItem(SNOOZE_KEY, until.toString());
    setSnoozedUntil(until);
    if (snoozeTimeoutRef.current) {
      clearTimeout(snoozeTimeoutRef.current);
    }
    snoozeTimeoutRef.current = setTimeout(
      () => setSnoozedUntil(null),
      SNOOZE_DURATION_MS,
    );
  }, []);

  const handleReload = useCallback(() => {
    window.localStorage.setItem(RELOAD_KEY, Date.now().toString());
    reloadWhenSafe();
  }, []);

  useEffect(() => {
    getCurrentBuildNumber().then((n) => {
      if (!n) {
        return;
      }
      const lastVersion = window.localStorage.getItem('version');

      // eslint-disable-next-line eqeqeq
      if (lastVersion === null || Number.parseInt(lastVersion, 10) !== n.n) {
        setIsFlashVisible(true);
        if (n) {
          window.localStorage.setItem('version', n.n.toString());
        }
      }
      setBuildNumber(n.n);
    });
  }, []);

  useEffect(() => {
    if (!buildNumber) {
      return;
    }
    const interval = setInterval(
      () => {
        getCurrentBuildNumber().then((n) => {
          if (!n) {
            return;
          }

          if (n.n !== buildNumber) {
            if (n.forceUpdate) {
              trackEvent('App - Automatically Updated');
              setTimeout(() => {
                reloadWhenSafe();
              }, 1000);
            } else {
              trackEvent('App - Soft Update Banner Shown');
              setHasUpdate(true);
            }
          }
        });
      },
      1000 * 60 * 5,
    );
    return () => {
      clearInterval(interval);
    };
  }, [buildNumber, trackEvent]);

  return (
    <VersionContext.Provider value={{ buildNumber }}>
      <Flash
        isFlashVisible={isFlashVisible}
        setIsFlashVisible={setIsFlashVisible}
        type="info"
        message="FleetOps has been updated"
      />
      {hasUpdate && !snoozedUntil && (
        <AppUpdateBanner onDismiss={handleDismiss} onReload={handleReload} />
      )}
      {children}
    </VersionContext.Provider>
  );
};

export default VersionProvider;
